import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import type { FilterConfig, FilterOption } from './Filter'
import FilterBar from './FilterBar'
import FilterButton from './FilterButton'
import FilterChips, { FilterChipDescriptor } from './FilterChips'
import { IconType } from '../icon/IconType'
import { SearchInput } from '../TeamFilters'
import { mqTo } from '../../styles/mediaQueries'

export interface IFilterDescriptor {
  key: string
  icon?: IconType
  label: string
  config: FilterConfig
  options: Array<FilterOption<string>>
  value: string[]
  onChange: (next: string[]) => void
}

export interface IFilterBundle {
  descriptors: IFilterDescriptor[]
  chips: FilterChipDescriptor[]
  hasActiveFilters: boolean
  clearAll: () => void
}

interface IProps {
  filters: IFilterBundle
  prependCustom?: React.ReactNode
  prependFilters?: IFilterDescriptor[]
  extraFilters?: IFilterDescriptor[]
  extraChips?: FilterChipDescriptor[]
  extraHasActive?: boolean
  onExtraClear?: () => void
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  searchPlaceholder?: string
  countLabel: string
  actions?: React.ReactNode
  leadingActions?: React.ReactNode
  className?: string
}

const FilterPanel: React.FC<IProps> = ({
  filters,
  prependCustom,
  prependFilters,
  extraFilters,
  extraChips,
  extraHasActive,
  onExtraClear,
  searchTerm,
  setSearchTerm,
  searchPlaceholder,
  countLabel,
  actions,
  leadingActions,
  className,
}) => {
  const { t } = useTranslation()

  const allChips = [...filters.chips, ...(extraChips ?? [])]
  const hasRemovable = filters.hasActiveFilters || !!extraHasActive

  const handleClearAll = () => {
    filters.clearAll()
    onExtraClear?.()
  }

  return (
    <StyledFilterBar
      className={className}
      label={t('general.forms.filters')}
      search={
        <StyledSearch
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          placeholder={searchPlaceholder}
          coloredBackground
        />
      }
      actions={actions}
      leadingActions={leadingActions}
      chips={
        <FilterChips
          chips={allChips}
          countLabel={countLabel}
          onClearAll={hasRemovable ? handleClearAll : undefined}
          clearAllLabel={t('general.forms.clearFilter')}
        />
      }
    >
      {prependCustom}
      {prependFilters?.map((d) => (
        <FilterButton<string>
          key={d.key}
          icon={d.icon}
          label={d.label}
          config={d.config}
          options={d.options}
          value={d.value}
          onChange={d.onChange}
        />
      ))}
      {filters.descriptors.map((d) => (
        <FilterButton<string>
          key={d.key}
          icon={d.icon}
          label={d.label}
          config={d.config}
          options={d.options}
          value={d.value}
          onChange={d.onChange}
        />
      ))}
      {extraFilters?.map((d) => (
        <FilterButton<string>
          key={d.key}
          icon={d.icon}
          label={d.label}
          config={d.config}
          options={d.options}
          value={d.value}
          onChange={d.onChange}
        />
      ))}
    </StyledFilterBar>
  )
}

const StyledFilterBar = styled(FilterBar)`
  margin-bottom: 24px;
`

const StyledSearch = styled(SearchInput)`
  margin: 0;
  width: 231px;

  ${mqTo.M`
    width: 100%;
  `}
`

export default FilterPanel
